import { computed, toValue } from "vue";
import { useGetPage } from "@/core/api/graphql/page";
import { useGetSlugInfo } from "@/core/api/graphql/slugInfo";
import { globals } from "@/core/globals";
import type { PageTemplate } from "@/shared/static-content";
import type { MaybeRefOrGetter } from "vue";

/**
 * @param seoUrl path after domain without slash at the beginning
 **/
export function useSlugInfo(
  seoUrl: MaybeRefOrGetter<string>,
  useCultureName: MaybeRefOrGetter<string | undefined> = undefined,
) {
  const { storeId, userId, cultureName } = globals;

  // Allow callers to request a specific content language (e.g. "de-DE" for German pages)
  // without changing the app-wide culture. Falls back to the global culture (English).
  const effectiveCultureName = computed(() => toValue(useCultureName) ?? cultureName);

  const variables = computed(() => {
    return {
      storeId,
      userId,
      cultureName: effectiveCultureName.value,
      slug: toValue(seoUrl),
    };
  });

  const { result, loading: slugLoading, error: slugError } = useGetSlugInfo(variables);

  const slugInfo = computed(() => {
    if (slugError.value) {
      return null;
    }
    return result.value?.slugInfo;
  });

  const objectType = computed(() => {
    return slugInfo.value?.entityInfo?.objectType;
  });

  const hasContent = computed(() => {
    return objectType.value === "ContentFile";
  });

  const {
    load: loadContent,
    result: contentResult,
    loading: contentLoading,
    error: contentError,
  } = useGetPage({ id: "", cultureName: effectiveCultureName.value, storeId });

  // Wrap loadContent to pass parameters explicitly instead of using reactive params
  const fetchContentSafe = async () => {
    const objectId = slugInfo?.value?.entityInfo?.objectId;
    if (!objectId) {
      return;
    }
    return await loadContent(undefined, { id: objectId, cultureName: effectiveCultureName.value, storeId });
  };

  const pageContent = computed(() => {
    if (contentError.value) {
      return null;
    }

    let content: unknown;
    if (typeof contentResult?.value?.page?.content === "string") {
      content = JSON.parse(contentResult.value.page.content);
    }

    if (isPageContent(content)) {
      return content;
    }

    return null;
  });

  function isPageContent(data: unknown): data is PageTemplate {
    return Array.isArray((data as PageTemplate)?.content) && typeof (data as PageTemplate)?.settings === "object";
  }

  return {
    loading: computed(() => {
      return slugLoading.value || contentLoading.value;
    }),
    slugInfo,
    objectType,
    hasContent,
    pageContent,
    fetchContent: fetchContentSafe,
    contentResult,
  };
}
